import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Camera, PenLine, RotateCcw, Trash2 } from 'lucide-react';
import apiClient from '../../utils/api';

export const DeliverShipment: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [shipment, setShipment] = useState<any>(null);
  const [form, setForm] = useState({ deliveryType: 'FULL', notes: '', location: '' });
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [returnMode, setReturnMode] = useState(false);
  const [returnReason, setReturnReason] = useState('');

  useEffect(() => {
    apiClient.getShipment(id!).then(r => setShipment(r.data.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  // Canvas signature
  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const pos = getPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true); setHasSig(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const pos = getPos(e, canvas);
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e293b';
    ctx.lineTo(pos.x, pos.y); ctx.stroke();
  };

  const stopDraw = () => setIsDrawing(false);

  const clearSig = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
  };

  const handlePhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    setPhotos(p => [...p, ...files]);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = ev => setPhotoPreviews(p => [...p, ev.target!.result as string]);
      reader.readAsDataURL(f);
    });
  };

  const removePhoto = (i: number) => {
    setPhotos(p => p.filter((_, idx) => idx !== i));
    setPhotoPreviews(p => p.filter((_, idx) => idx !== i));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('deliveryType', form.deliveryType);
      if (form.notes) fd.append('notes', form.notes);
      if (form.location) fd.append('location', form.location);
      if (hasSig && canvasRef.current) fd.append('podSignature', canvasRef.current.toDataURL('image/png'));
      photos.forEach(f => fd.append('photos', f));

      await apiClient.deliverShipmentWithPOD(id!, fd);
      navigate(basePath);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to mark as delivered');
    } finally { setSaving(false); }
  };

  const submitReturn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!returnReason.trim()) return setError('Return reason is required');
    setSaving(true); setError('');
    try {
      await apiClient.requestReturn(id!, returnReason);
      navigate(basePath);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to submit return request');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!shipment) return <div className="empty-state"><h3>Shipment not found</h3></div>;

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
          <h1 className="page-title">{returnMode ? 'Request Return' : 'Mark as Delivered'}</h1>
        </div>
        {shipment.status === 'DELIVERED' && !returnMode && (
          <button className="btn btn-ghost" style={{ color: '#f59e0b' }} onClick={() => setReturnMode(true)}>
            <RotateCcw size={15} /> Request Return
          </button>
        )}
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Shipment info */}
        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Shipment Info</h2>
          {[
            ['Tracking #', shipment.trackingNumber],
            ['Sender', shipment.senderName],
            ['Receiver', shipment.receiverName],
            ['Destination', `${shipment.receiverAddress}${shipment.receiverCity ? ', ' + shipment.receiverCity : ''}`],
            ['Amount', `${Number(shipment.totalAmount).toLocaleString()} ${shipment.currency}`],
            ['Status', shipment.status],
          ].map(([k, v]) => (
            <div key={k as string} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>{k}</span>
              <span style={{ fontWeight: 500, fontSize: 13 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Delivery / Return form */}
        <div className="card">
          {returnMode ? (
            <>
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Return Request</h2>
              <form onSubmit={submitReturn}>
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Return Reason *</label>
                  <textarea rows={4} value={returnReason} onChange={e => setReturnReason(e.target.value)} placeholder="Describe why this shipment is being returned..." required />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" className="btn btn-ghost" onClick={() => setReturnMode(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1 }}>
                    {saving ? 'Submitting...' : 'Submit Return Request'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Delivery Details</h2>
              <form onSubmit={submit}>
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Delivery Type *</label>
                  <select value={form.deliveryType} onChange={e => setForm(f => ({ ...f, deliveryType: e.target.value }))}>
                    <option value="FULL">Full Delivery</option>
                    <option value="PARTIAL">Partial Delivery</option>
                    <option value="REJECT">Rejected</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Delivery Location</label>
                  <input placeholder="e.g. Kigali, Nyarugenge" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Notes</label>
                  <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>

                {/* Signature pad */}
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <label style={{ margin: 0 }}><PenLine size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />Signature (optional)</label>
                    {hasSig && <button type="button" className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }} onClick={clearSig}>Clear</button>}
                  </div>
                  <canvas
                    ref={canvasRef}
                    width={340} height={100}
                    style={{ border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'crosshair', touchAction: 'none', background: '#fafafa', width: '100%' }}
                    onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                    onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                  />
                </div>

                {/* Photo upload */}
                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label><Camera size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />Photos (max 5, optional)</label>
                  {photoPreviews.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      {photoPreviews.map((src, i) => (
                        <div key={i} style={{ position: 'relative' }}>
                          <img src={src} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
                          <button type="button" onClick={() => removePhoto(i)} style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Trash2 size={10} color="#fff" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {photos.length < 5 && (
                    <input type="file" accept="image/*" multiple onChange={handlePhotos} style={{ fontSize: 13 }} />
                  )}
                </div>

                <button type="submit" className="btn btn-success" disabled={saving} style={{ width: '100%' }}>
                  <CheckCircle size={15} /> {saving ? 'Processing...' : 'Confirm Delivery'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
};
